// AutomationPreviewGenerator.ts
// This file converts technical automation configuration into human-readable preview text
// Console logs help track the conversion process for debugging

import logger from "../../utils/logger";

interface PreviewData {
  trigger: string;
  conditions: string;
  timeline: Array<{
    day: number;
    dayLabel: string;
    actions: string[];
    delay?: number;
    delayText?: string;
  }>;
  summary: string;
  stats: {
    totalActions: number;
    totalDays: number;
    estimatedDuration: string;
  };
}

interface AutomationCondition {
  id: string;
  fieldId: string;
  operator: string;
  value: string;
  customFieldValue?: string;
}

export class AutomationPreviewGenerator {
  private automationData: any;
  private dayActions: any;
  private dayDelays: any;
  private fieldConditions: AutomationCondition[];
  private timeOffsetConfig: any;
  private isTimeBasedEvent: boolean;

  // Source data mappings - these are loaded from your existing constants
  private sourceData: any[] = [];
  private statusLabelData: any[] = [];
  private taskData: any[] = [];
  private userListData: any[] = [];
  private leadListData: any[] = [];

  constructor(
    automationData: any,
    dayActions: any,
    dayDelays: any,
    fieldConditions: AutomationCondition[],
    timeOffsetConfig: any,
    isTimeBasedEvent: boolean,
    // Additional data for mapping IDs to names
    sourceData: any[] = [],
    statusLabelData: any[] = [],
    taskData: any[] = [],
    userListData: any[] = [],
    leadListData: any[] = []
  ) {
    logger.log('[AutomationPreviewGenerator] Initializing with data:', {
      automationData,
      dayActions,
      dayDelays,
      fieldConditions,
      timeOffsetConfig,
      isTimeBasedEvent
    });

    this.automationData = automationData;
    this.dayActions = dayActions;
    this.dayDelays = dayDelays;
    this.fieldConditions = fieldConditions || [];
    this.timeOffsetConfig = timeOffsetConfig;
    this.isTimeBasedEvent = isTimeBasedEvent;
    this.sourceData = sourceData;
    this.statusLabelData = statusLabelData;
    this.taskData = taskData;
    this.userListData = userListData;
    this.leadListData = leadListData;
  }

  /**
   * Main method to generate the complete preview data
   */
  generate(): PreviewData {
    logger.log('[AutomationPreviewGenerator] Starting preview generation...');

    const trigger = this.generateTriggerText();
    const conditions = this.generateConditionsText();
    const timeline = this.generateTimelineText();
    const summary = this.generateSummaryText();
    const stats = this.calculateStats();

    const previewData: PreviewData = {
      trigger,
      conditions,
      timeline,
      summary,
      stats
    };

    logger.log('[AutomationPreviewGenerator] Generated preview data:', previewData);
    return previewData;
  }

  /**
   * Generate human-readable trigger description
   */
  private generateTriggerText(): string {
    logger.log('[AutomationPreviewGenerator] Generating trigger text for event:', this.automationData.event);

    // Map technical event names to human-readable descriptions
    const triggerMap: { [key: string]: string } = {
      'create_lead': 'a new lead is created',
      'edit_lead': 'lead information is updated',
      'status_change': 'a lead status changes',
      'label_change': 'a lead label is updated',
      'create_quotation': 'a new quotation is created',
      'edit_quotation': 'a quotation is updated',
      'create_invoice': 'a new invoice is created',
      'edit_invoice': 'an invoice is updated',
      'create_order': 'a new order is created',
      'edit_order': 'an order is updated',
      'create_task': 'a new task is created',
      'edit_task': 'a task is updated',
      'task_due_date': 'a task due date approaches',
      'task_completed': 'a task is completed',
      'create_activity': 'a new activity is logged',
      'call_duration': 'a call meets duration criteria',
      'call_count': 'call count reaches threshold',
      'new_lead_added': 'a lead is added to a list',
      'view_page': 'a lead views a page',
      'view_file': 'a lead views a file'
    };

    let triggerText = `When ${triggerMap[this.automationData.event] || this.automationData.event}`;
    logger.log('[AutomationPreviewGenerator] Base trigger text:', triggerText);

    // Add source/target specification if exists
    if (this.automationData.targetValues && this.automationData.targetValues !== 'any') {
      const sourceDescription = this.getSourceDescription();
      if (sourceDescription) {
        triggerText += ` ${sourceDescription}`;
        logger.log('[AutomationPreviewGenerator] Added source description:', sourceDescription);
      }
    }

    // Add time offset for time-based events
    if (this.isTimeBasedEvent && this.timeOffsetConfig) {
      const timeDescription = this.getTimeOffsetDescription();
      if (timeDescription) {
        triggerText += ` ${timeDescription}`;
        logger.log('[AutomationPreviewGenerator] Added time offset:', timeDescription);
      }
    }

    logger.log('[AutomationPreviewGenerator] Final trigger text:', triggerText);
    return triggerText;
  }

  /**
   * Generate description for source/target values
   */
  private getSourceDescription(): string {
    const event = this.automationData.event;
    const targetValue = this.automationData.targetValues;

    logger.log('[AutomationPreviewGenerator] Getting source description for:', { event, targetValue });

    switch (event) {
      case 'create_lead':
        return this.getLeadSourceDescription(targetValue);
      case 'status_change':
      case 'label_change':
        return this.getStatusLabelDescription(targetValue, event);
      case 'create_task':
      case 'task_due_date':
      case 'task_completed':
        return this.getTaskTypeDescription(targetValue);
      case 'new_lead_added':
        return this.getLeadListDescription(targetValue);
      default:
        return '';
    }
  }

  /**
   * Get lead source description
   */
  private getLeadSourceDescription(targetValue: string): string {
    if (targetValue === 'any') return 'from any source';
    
    const source = this.sourceData.find(s => s._id === targetValue);
    const sourceName = source ? source.name : 'Unknown Source';
    logger.log('[AutomationPreviewGenerator] Lead source found:', sourceName);
    return `from ${sourceName}`;
  }

  /**
   * Get status/label description
   */
  private getStatusLabelDescription(targetValue: string, event: string): string {
    if (targetValue === 'any') return event === 'status_change' ? 'to any status' : 'to any label';
    
    const item = this.statusLabelData.find(s => s.value === targetValue);
    const itemName = item ? item.name : targetValue;
    const prefix = event === 'status_change' ? 'to' : 'with';
    logger.log('[AutomationPreviewGenerator] Status/Label found:', itemName);
    return `${prefix} "${itemName}"`;
  }

  /**
   * Get task type description
   */
  private getTaskTypeDescription(targetValue: string): string {
    if (targetValue === 'any') return 'of any type';
    
    const task = this.taskData.find(t => t.value === targetValue);
    const taskName = task ? task.name : targetValue;
    logger.log('[AutomationPreviewGenerator] Task type found:', taskName);
    return `of type "${taskName}"`;
  }

  /**
   * Get lead list description
   */
  private getLeadListDescription(targetValue: string): string {
    const list = this.leadListData.find(l => l._id === targetValue);
    const listName = list ? list.name : 'Unknown List';
    logger.log('[AutomationPreviewGenerator] Lead list found:', listName);
    return `to list "${listName}"`;
  }

  /**
   * Get time offset description for time-based events
   */
  private getTimeOffsetDescription(): string {
    if (!this.timeOffsetConfig.amount || !this.timeOffsetConfig.operation) {
      return '';
    }

    const amount = this.timeOffsetConfig.amount;
    const operation = this.timeOffsetConfig.operation;
    const dayText = amount === 1 ? 'day' : 'days';
    
    return `(${amount} ${dayText} ${operation})`;
  }

  /**
   * Generate field conditions description
   */
  private generateConditionsText(): string {
    if (!this.fieldConditions || this.fieldConditions.length === 0) {
      logger.log('[AutomationPreviewGenerator] No field conditions found');
      return '';
    }

    logger.log('[AutomationPreviewGenerator] Generating conditions text for:', this.fieldConditions);

    const conditionTexts = this.fieldConditions.map((condition, index) => {
      const fieldName = this.getFriendlyFieldName(condition.fieldId, condition.customFieldValue);
      const operatorText = this.getFriendlyOperator(condition.operator);
      const conditionText = `${fieldName} ${operatorText} "${condition.value}"`;
      
      logger.log(`[AutomationPreviewGenerator] Condition ${index + 1}:`, conditionText);
      return conditionText;
    });

    const result = `where ${conditionTexts.join(' AND ')}`;
    logger.log('[AutomationPreviewGenerator] Final conditions text:', result);
    return result;
  }

  /**
   * Convert technical field IDs to friendly names
   */
  private getFriendlyFieldName(fieldId: string, customFieldValue?: string): string {
    logger.log('[AutomationPreviewGenerator] Converting field ID to friendly name:', { fieldId, customFieldValue });

    // Handle custom field entries
    if (fieldId === 'custom_field_entry' && customFieldValue) {
      return customFieldValue;
    }

    // Map technical field names to user-friendly names
    const fieldMap: { [key: string]: string } = {
      'extraDetails.duration': 'Call Duration (seconds)',
      'count': 'Call Count',
      'total': 'Total Amount',
      'status': 'Status',
      'name': 'Lead Name',
      'email': 'Email Address',
      'phone': 'Phone Number',
      'company': 'Company Name',
      'notes': 'Notes'
    };

    const friendlyName = fieldMap[fieldId] || fieldId;
    logger.log('[AutomationPreviewGenerator] Mapped field name:', friendlyName);
    return friendlyName;
  }

  /**
   * Convert technical operators to friendly text
   */
  private getFriendlyOperator(operator: string): string {
    const operatorMap: { [key: string]: string } = {
      'equals': 'is equal to',
      'not_equals': 'is not equal to',
      'greater_than': 'is greater than',
      'greater_than_equal': 'is greater than or equal to',
      'less_than': 'is less than',
      'less_than_equal': 'is less than or equal to',
      'contains': 'contains',
      'not_contains': 'does not contain'
    };

    return operatorMap[operator] || operator;
  }

  /**
   * Generate timeline with actions and delays
   */
  private generateTimelineText(): Array<{
    day: number;
    dayLabel: string;
    actions: string[];
    delay?: number;
    delayText?: string;
  }> {
    logger.log('[AutomationPreviewGenerator] Generating timeline from dayActions:', this.dayActions);

    const timeline: Array<{
      day: number;
      dayLabel: string;
      actions: string[];
      delay?: number;
      delayText?: string;
    }> = [];

    // Sort days by number
    const sortedDays = Object.entries(this.dayActions)
      .sort(([a], [b]) => parseInt(a.split('_')[1]) - parseInt(b.split('_')[1]));

    logger.log('[AutomationPreviewGenerator] Sorted days:', sortedDays.map(([key]) => key));

    sortedDays.forEach(([dayKey, actions], index) => {
      const dayNum = parseInt(dayKey.split('_')[1]);
      
      // Generate day label
      let dayLabel: string;
      if (index === 0) {
        dayLabel = 'Immediately';
      } else {
        dayLabel = `Day ${dayNum}`;
      }

      // Generate action descriptions
      const actionDescriptions = (actions as any[]).map((action, actionIndex) => {
        const description = this.getActionDescription(action);
        logger.log(`[AutomationPreviewGenerator] Action ${actionIndex + 1} on ${dayKey}:`, description);
        return description;
      });

      // Get delay information
      const delay = this.dayDelays[dayKey];
      let delayText: string | undefined;
      if (delay && index < sortedDays.length - 1) {
        delayText = `Wait ${delay} ${delay === 1 ? 'day' : 'days'}`;
        logger.log(`[AutomationPreviewGenerator] Delay after ${dayKey}:`, delayText);
      }

      timeline.push({
        day: dayNum,
        dayLabel,
        actions: actionDescriptions,
        delay,
        delayText
      });
    });

    logger.log('[AutomationPreviewGenerator] Generated timeline:', timeline);
    return timeline;
  }

  /**
   * Generate human-readable action description
   */
  private getActionDescription(action: any): string {
    logger.log('[AutomationPreviewGenerator] Generating description for action:', action);

    const actionType = action.type;
    const actionData = action.data || {};

    switch (actionType) {
      case 'send_whatsapp':
        return this.getWhatsAppActionDescription(actionData);
      case 'send_email':
      case 'send_gmail':
      case 'send_zeptomail':
        return this.getEmailActionDescription(actionData, actionType);
      case 'create_task':
        return this.getTaskActionDescription(actionData);
      case 'status_change':
        return this.getStatusChangeDescription(actionData);
      case 'label_change':
        return this.getLabelChangeDescription(actionData);
      case 'assign_lead':
        return this.getAssignLeadDescription(actionData);
      case 'push_notification':
        return this.getNotificationDescription(actionData);
      case 'move_to_list':
      case 'copy_to_list':
        return this.getListActionDescription(actionData, actionType);
      case 'add_to_google_sheet':
        return 'Add lead data to Google Sheet (auto-created)';
      case 'add_facebook_audience':
        return this.getFacebookAudienceDescription(actionData);
      case 'webhook':
        return this.getWebhookActionDescription(actionData);
      default:
        logger.log('[AutomationPreviewGenerator] Unknown action type:', actionType);
        return `Perform ${actionType} action`;
    }
  }

  /**
   * WhatsApp action description
   */
  private getWhatsAppActionDescription(actionData: any): string {
    if (actionData.templateData && actionData.templateData.name) {
      return `📱 Send WhatsApp message using template "${actionData.templateData.name}"`;
    } else if (actionData.template) {
      return `📱 Send WhatsApp message (Template ID: ${actionData.template})`;
    }
    return '📱 Send WhatsApp message';
  }

  /**
   * Email action description
   */
  private getEmailActionDescription(actionData: any, actionType: string): string {
    const emailType = actionType === 'send_gmail' ? 'Gmail' : 
                     actionType === 'send_zeptomail' ? 'ZeptoMail' : 
                     'Email';
    
    if (actionData.templateData && actionData.templateData.details?.title) {
      return `📧 Send ${emailType} using template "${actionData.templateData.details.title}"`;
    } else if (actionData.content) {
      return `📧 Send ${emailType} (Template ID: ${actionData.content})`;
    }
    return `📧 Send ${emailType}`;
  }

  /**
   * Task creation action description
   */
  private getTaskActionDescription(actionData: any): string {
    let description = '✅ Create task';
    
    if (actionData.type) {
      const taskType = this.taskData.find(t => t.value === actionData.type);
      const taskTypeName = taskType ? taskType.name : actionData.type;
      description += ` of type "${taskTypeName}"`;
    }
    
    if (actionData.assignedTo && actionData.assignedTo.length > 0) {
      const userId = Array.isArray(actionData.assignedTo) ? actionData.assignedTo[0] : actionData.assignedTo;
      const user = this.userListData.find(u => u._id === userId);
      const userName = user ? user.firstName : 'Unknown User';
      description += ` assigned to ${userName}`;
    }
    
    if (actionData.notes) {
      description += ` with note: "${actionData.notes}"`;
    }
    
    return description;
  }

  /**
   * Status change action description
   */
  private getStatusChangeDescription(actionData: any): string {
    if (actionData.status) {
      const status = this.statusLabelData.find(s => s.value === actionData.status);
      const statusName = status ? status.name : actionData.status;
      return `🔄 Change lead status to "${statusName}"`;
    }
    return '🔄 Change lead status';
  }

  /**
   * Label change action description
   */
  private getLabelChangeDescription(actionData: any): string {
    if (actionData.label && actionData.label.length > 0) {
      const labelValue = Array.isArray(actionData.label) ? actionData.label[0] : actionData.label;
      const label = this.statusLabelData.find(l => l.value === labelValue);
      const labelName = label ? label.name : labelValue;
      return `🏷️ Add label "${labelName}" to lead`;
    }
    return '🏷️ Change lead label';
  }

  /**
   * Assign lead action description
   */
  private getAssignLeadDescription(actionData: any): string {
    if (actionData.assignToUser) {
      const user = this.userListData.find(u => u._id === actionData.assignToUser);
      const userName = user ? user.firstName : 'Unknown User';
      return `👤 Assign lead to ${userName}`;
    }
    return '👤 Assign lead to user';
  }

  /**
   * Notification action description
   */
  private getNotificationDescription(actionData: any): string {
   if (actionData.push_notification) {
  const user = this.userListData.find(u => u._id === actionData.push_notification);
  const userName = user ? user.firstName : 'Unknown User';
  return `🔔 Send notification to ${userName}`;
}
    return '🔔 Send notification';
  }

  /**
   * List action (move/copy) description
   */
  private getListActionDescription(actionData: any, actionType: string): string {
    const actionText = actionType === 'move_to_list' ? 'Move' : 'Copy';
    
    if (actionData.targetListId) {
      const list = this.leadListData.find(l => l._id === actionData.targetListId);
      const listName = list ? list.name : 'Unknown List';
      return `📋 ${actionText} lead to list "${listName}"`;
    }
    return `📋 ${actionText} lead to list`;
  }

  /**
   * Facebook audience action description
   */
  private getFacebookAudienceDescription(actionData: any): string {
    if (actionData.audienceName) {
      return `📘 Add lead to Facebook audience "${actionData.audienceName}"`;
    }
    return '📘 Add lead to Facebook audience';
  }

  /**
   * Webhook action description
   */
  private getWebhookActionDescription(actionData: any): string {
    if (actionData.webhookUrl) {
      const method = actionData.requestMethod || 'POST';
      try {
        const url = new URL(actionData.webhookUrl);
        return `🔗 Send ${method} webhook to ${url.hostname}`;
      } catch (e) {
        return `🔗 Send ${method} webhook to ${actionData.webhookUrl}`;
      }
    }
    return '🔗 Trigger webhook';
  }

  /**
   * Generate overall summary text
   */
  private generateSummaryText(): string {
    const totalActions = Object.values(this.dayActions).flat().length;
    const maxDay = Math.max(...Object.keys(this.dayActions).map(k => parseInt(k.split('_')[1])));
    
    let summary = `This automation will be triggered ${this.generateTriggerText()}`;
    
    if (this.fieldConditions.length > 0) {
      summary += ` ${this.generateConditionsText()}`;
    }
    
    summary += `. It will execute ${totalActions} action${totalActions !== 1 ? 's' : ''}`;
    
    if (maxDay > 1) {
      summary += ` over ${maxDay} day${maxDay !== 1 ? 's' : ''}`;
    }
    
    summary += '. Each matching lead will go through this sequence automatically.';
    
    logger.log('[AutomationPreviewGenerator] Generated summary:', summary);
    return summary;
  }

  /**
   * Calculate automation statistics
   */
  private calculateStats() {
    const totalActions = Object.values(this.dayActions).flat().length;
    const dayNumbers = Object.keys(this.dayActions).map(k => parseInt(k.split('_')[1]));
    const totalDays = dayNumbers.length > 0 ? Math.max(...dayNumbers) : 1;
    
    let estimatedDuration: string;
    if (totalDays === 1) {
      estimatedDuration = 'Immediate';
    } else if (totalDays <= 7) {
      estimatedDuration = `${totalDays} days`;
    } else if (totalDays <= 30) {
      estimatedDuration = `${Math.ceil(totalDays / 7)} weeks`;
    } else {
      estimatedDuration = `${Math.ceil(totalDays / 30)} months`;
    }

    const stats = {
      totalActions,
      totalDays,
      estimatedDuration
    };

    logger.log('[AutomationPreviewGenerator] Calculated stats:', stats);
    return stats;
  }
}